import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import logo from "@/assets/logo.png";

const HERO = "https://images.unsplash.com/photo-1753194931766-211c9f563156?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxmbG9yaWRhJTIwdmFjYXRpb24lMjBob21lJTIwZXh0ZXJpb3IlMjBwb29sfGVufDB8fHx8MTc4MTc5MDU5MXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center px-6 sm:px-12 lg:px-20 bg-[#FDFBF7]">
        <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="mb-10">
            <img src={logo} alt="Magical Villas Orlando — Luxury Homes" className="h-36 w-auto" />
          </div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-gray-500 mb-3">Manager Portal</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none font-medium text-[#111827] mb-3">
            Welcome back.
          </h1>
          <p className="text-gray-500 mb-8">Sign in to manage your vacation homes, reservations and guests.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Email</label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1A365D] focus:border-transparent transition-all outline-none"
                placeholder="you@example.com"
                required
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1A365D] focus:border-transparent transition-all outline-none"
                placeholder="••••••••"
                required
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
            )}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#1A365D] text-white hover:bg-[#2A4A7F] rounded-full px-6 py-3 font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading && <Loader2 className="animate-spin" size={18} />}
              Sign In
            </button>
          </form>
        </div>
      </div>
      <div className="hidden lg:block flex-1 relative">
        <img src={HERO} alt="Florida vacation home" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A365D]/70 via-[#1A365D]/10 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="font-display text-3xl font-medium leading-tight">Sunshine state hospitality, beautifully managed.</p>
        </div>
      </div>
    </div>
  );
}
